/**
 * Search controller for the full search view.
 */

/* global $, _ */
import Debug from 'ace-ui/ace-debug';

const COMPONENT_NAME = 'FullSearchController';
const debug = new Debug (COMPONENT_NAME);

export default function ContentDeveloperFullSearchController
($scope, $location, $routeParams, SearchService, ClipboardService, ContentCardService,
  ContentEditor, ApplicationConfigurationService, ErrorMessagingService, templates) {

  ErrorMessagingService.clearErrors();

  let currentSearchRequestId = 0;
  let knownTypes = getTypeNames (templates);
  let facetStartAmount = 10;
  let itemsPerPage = 50;
  let selectedFacetFields = $routeParams.facets && JSON.parse($routeParams.facets) || {};
  let start = 0;

  let State = {
    Idle: 'idle',
    Searching: 'search',
    Error: 'error'
  };

  $scope.states = State;
  $scope.state = State.Idle;

  $scope.defaultFacets = {
    'ace_type_s': 'Type',
    'ace_workflowstate_state_s': 'Workflow state',
    'ace_workflowstate_workflowId_s': 'Workflow'
  };

  $scope.query = !$routeParams.q ? '*:*' : `${$routeParams.q} OR ${$routeParams.q}*`;
  $scope.showTrash = !!$routeParams.trash;

  var isFullSearch = $routeParams.q === '*' || $routeParams.q === '**' || $routeParams.q === '*:*';

  // For full searches relevance has no meaning, so we go with date
  $scope.sorter = isFullSearch ? 'date' : 'relevance'

  $scope.open = (searchEntry) => {
    $location.search(' ');
    ContentEditor.openContent(searchEntry.id);
  };

  $scope.toggleShowTrash = () => {
    if ($scope.showTrash) {
      performSearch().then(() => { $scope.showTrash = false; });
    } else {
      performSearch(false, true).then(() => { $scope.showTrash = true; });
    }
  };

  $scope.toggleFacetField = (tagName, facetField) => {
    facetField.selected = !facetField.selected;

    let tagEntry = selectedFacetFields[tagName] || [];

    tagEntry = tagEntry.filter((selectedFacetValue) => {
      return selectedFacetValue.fieldValue !== facetField.fieldValue;
    });

    if (facetField.selected) {
      tagEntry.push({
        fieldValue: facetField.fieldValue
      });
    }

    selectedFacetFields[tagName] = tagEntry;

    if (_.isEmpty(tagEntry)) {
      selectedFacetFields = _.omit(selectedFacetFields, tagName);
    }

    let queryString = { q: $routeParams.q };

    if (!_.isEmpty(selectedFacetFields)) {
      queryString.facets = JSON.stringify(selectedFacetFields);
    }

    if ($scope.showTrash) {
      queryString.trash = 'true';
    }

    $location.search(queryString);
  };

  $scope.showMoreFacetFields = (facetField) => {
    facetField.showing *= 2;
  };

  $scope.search = () => {
    if ($scope.query) {
      if ($scope.facetFields) {
        performSearch(false, $scope.showTrash);
      } else {
        ApplicationConfigurationService.getConfigurationsFor(['Search'])
          .then((configuration) => {
            $scope.facetConfigs = configuration[0].faceting;
            if (configuration[0].maxFacetValues) {
              $scope.globalMaxFacet = configuration[0].maxFacetValues;
            } else {
              $scope.globalMaxFacet = -1;
            }

            $scope.facetFields = {};
            $scope.fieldsMaxFacet = {};

            $scope.facetConfigs.forEach((fieldCfg) => {
              $scope.facetFields[fieldCfg.name] = fieldCfg.label;
              if (fieldCfg.maxFacetValues) {
                $scope.fieldsMaxFacet[`f.${fieldCfg.name}.facet.limit`] = fieldCfg.maxFacetValues;
              }
            });

            return performSearch(false, $scope.showTrash);
          })
          .catch(() => {
            if (!$scope.facetFields) {
              $scope.facetFields = $scope.defaultFacets;
              $scope.globalMaxFacet = -1;
              performSearch(false, $scope.showTrash);
            }
          });
      }
    } else {
      $scope.state = State.Idle;
    }
  };

  $scope.loadMore = () => {
    start += itemsPerPage;
    performSearch(true, $scope.showTrash);
  };

  $scope.$watch('state', () => {
    if ($scope.state == State.Idle) {
      if ($scope.searchResults == null) {
        $scope.statusText = '';
      } else if ($scope.searchResults.length == 0) {
        $scope.statusText = 'full-search.status.no-result.text';
      } else {
        $scope.statusText = 'full-search.status.showing.text';
      }
    } else if ($scope.state == State.Searching) {
      $scope.statusText = 'full-search.status.searching.text';
    } else if ($scope.state == State.Error) {
      $scope.statusText = 'full-search.status.server-error.text';
    }
  });

  $scope.copy = (event, searchEntry) => {
    copy(event, [{
      id: searchEntry.alias,
      type: searchEntry.contentType.value,
      href: searchEntry.href
    }]);
  };

  $scope.copyAll = (event) => {
    let clipboardData = ($scope.searchResults || []).map((result) => {
      return {
        id: result.id,
        type: result.contentCard.contentType.value,
        href: result.href
      };
    });

    copy(event, clipboardData);
  };

  /**
   * Init
   */
  $scope.search();

  // Helpers:
  function copy (event, clipboardData) {
    event.stopPropagation();
    event.preventDefault();

    ClipboardService.putInClipboard(clipboardData)
      .catch((error) => {
        console.warn('Error: Failed to insert content reference(s) into clipboard!', error);

        $(event.target).attr('title', 'Copy failed!');
        $(event.target).tooltip({
          'trigger': 'manual',
          'container': 'body'
        });
        $(event.target).tooltip('show');

        setTimeout(() => {
          $(event.target).tooltip('destroy');
        }, 2000);
      });
  }

  function facetSort (facetValue, otherFacetValue) {
    if (otherFacetValue.selected && !facetValue.selected) {
      return 1;
    } else if (facetValue.selected && !otherFacetValue.selected) {
      return -1;
    }

    if (otherFacetValue.fieldOccurrences > facetValue.fieldOccurrences) {
      return 1;
    } else if (otherFacetValue.fieldOccurrences < facetValue.fieldOccurrences) {
      return -1;
    }

    return facetValue.fieldValue.localeCompare(otherFacetValue.fieldValue);
  }

  function filterQuery () {
    let str = '';

    Object.keys(selectedFacetFields)
      .forEach((tagName) => {
        let facets = selectedFacetFields[tagName];

        if (facets.length > 0) {
          str += '+' + tagName + ':(';

          let index = 0;
          facets.forEach((value) => {
            if (facets.length > 0 && index > 0) {
              str += 'AND ';
            }

            str += '"' + value.fieldValue + '" ';

            index++;
          });

          str += ') ';
        }
      });

    return str;
  }

  function getSearchQuery ({ queryText, onlyHidden }) {
    let searchParams = {
      'q': queryText,
      'qt': 'freetextSearch',
      'fq': [filterQuery(), '+ace_type_s:(' + knownTypes.join(' OR ') + ')'],
      'facet': 'true',
      'facet.limit': $scope.globalMaxFacet,
      'facet.field': Object.keys($scope.facetFields),
      'facet.mincount': 1,
      'start': start,
      'rows': itemsPerPage
    };

    debug.log ('searchParams', searchParams);

    if (onlyHidden) {
      searchParams.fq.push('+ace_contentstate_hidden_b:true');
    }

    if ($scope.sorter === 'date') {
      searchParams.sort = 'ace_modificationTime_dt desc';
    }

    for (let facetLimit in $scope.fieldsMaxFacet) {
      searchParams[facetLimit] = $scope.fieldsMaxFacet[facetLimit];
    }

    return {
      'variant': 'aceUISearch',
      'params': searchParams
    };
  }

  function getTypeNames (templates) {
    return templates.reduce((arr, template) => {
      if (arr.indexOf(template.type) === -1) {
        arr.push(template.type);
      }
      return arr;
    }, []);
  }

  function performSearch (extendResult, onlyHidden) {
    let searchRequestId = ++currentSearchRequestId;
    $scope.state = State.Searching;

    return SearchService.search(getSearchQuery({ queryText: $scope.query, onlyHidden }))
      .then((searchResponse) => {

        if (searchRequestId === currentSearchRequestId) {
          let searchResults = processDocuments(atex.aceui.ObjectUtils.getByPath(searchResponse, 'response/docs') || []),
            facetResults = processFacets(atex.aceui.ObjectUtils.getByPath(searchResponse, 'facet_counts/facet_fields') || []);

          $scope.numFound = atex.aceui.ObjectUtils.getByPath(searchResponse, 'response/numFound');
          $scope.searchResults = (extendResult === true) ?
            ($scope.searchResults || []).concat(searchResults) :
            searchResults;
          $scope.facets = facetResults;
          $scope.state = State.Idle;
        }

      })
      .catch((error) => {
        console.warn('Error while searching!', error);
        $scope.state = State.Error;
      });
  }

  function processDocuments (searchDocuments) {
    let searchResults = [];

    ContentCardService.fromAceUISearch (searchDocuments.map(doc => doc._content))
      .forEach((aceUISearchResult) => {
        let entry = {
          id: aceUISearchResult.alias,
          href: ContentEditor.getContentLink (aceUISearchResult.alias)
        };

        let contentTypeLabel = aceUISearchResult.contentType.label || aceUISearchResult.contentType.value;
        entry.contentCard = aceUISearchResult.getContextual ({ contentType: { label:  contentTypeLabel }, thumbnail: { aspectRatio: '3:2', height: 60 }}, 'CARD', 'SEARCH-VIEW', { thumbnail: { collapse: false }});

        ContentCardService.resolveThumbnail (entry.contentCard.thumbnail)
          .then (thumbnail => { entry.contentCard.thumbnail = thumbnail; });

        searchResults.push (entry);
      });

    return searchResults;
  }

  function processFacets (fields) {
    return Object.keys(fields).map((field) => {
      return {
        field: field,
        name: $scope.facetFields[field],
        showing: facetStartAmount,
        values: ((arr, newArr) => {
          for (let i = 0; i < arr.length; i += 2) {
            newArr.push({
              fieldValue: arr[i],
              fieldOccurrences: arr[i + 1],
              selected: !!(selectedFacetFields[field] || []).find((item) => item.fieldValue === arr[i])
            });
          }
          return newArr.sort(facetSort);
        })(fields[field], [])
      };
    });
  }
}

ContentDeveloperFullSearchController.resolves = {
  templates: (TemplateService) => {
    return TemplateService.getTemplates('Read');
  }
};
